/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminWorkAndRestRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IOvertimeType } from '../../models/overtime-type.model';
import { OvertimeTypeApiService } from '../../services/overtime-type-api.service';
import {
  CreateOvertimeType,
  DeleteOvertimeType,
  LoadAllOvertimeTypes,
  LoadOvertimeTypeById,
  LoadOvertimeTypes,
  UpdateOvertimeType,
  ClearCurrentOvertimeType,
} from './overtime-type.actions';

export interface OvertimeTypeStateModel {
  allOvertimeTypes: IOvertimeType[];
  overtimeTypes: IOvertimeType[];
  overtimeTypesLoaded: boolean;
  currentOvertimeType: IOvertimeType | undefined;
  overtimeTypesTotalPages: number;
}

const OVERTIME_TYPE_STATE_TOKEN = new StateToken<OvertimeTypeStateModel>('overtimeType');

@State<OvertimeTypeStateModel>({
  name: OVERTIME_TYPE_STATE_TOKEN,
  defaults: {
    allOvertimeTypes: [],
    overtimeTypes: [],
    overtimeTypesLoaded: false,
    overtimeTypesTotalPages: 0,
    currentOvertimeType: undefined,
  },
})
@Injectable()
export class OvertimeTypeState {
  constructor(private service: OvertimeTypeApiService) {}

  @Action(LoadOvertimeTypes)
  loadOvertimeTypes(
    { getState, setState }: StateContext<OvertimeTypeStateModel>,
    { filter }: LoadOvertimeTypes
  ) {
    return this.service.loadOvertimeTypes(filter).pipe(
      tap((response: ApiResponse<IOvertimeType[]>) => {
        const state = getState();
        setState({
          ...state,
          overtimeTypesLoaded: true,
          overtimeTypes: response.data,
          overtimeTypesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllOvertimeTypes)
  loadAllOvertimeTypes({ getState, setState }: StateContext<OvertimeTypeStateModel>) {
    return this.service.loadAllOvertimeTypes().pipe(
      tap((response: IOvertimeType[]) => {
        const state = getState();
        setState({
          ...state,
          allOvertimeTypes: response,
        });
      })
    );
  }

  @Action(LoadOvertimeTypeById, { cancelUncompleted: true })
  loadOvertimeTypeById(
    { setState }: StateContext<OvertimeTypeStateModel>,
    { id }: LoadOvertimeTypeById
  ) {
    return this.service.loadOvertimeTypeById(id).pipe(
      tap((overtimeType: IOvertimeType) => {
        setState(
          patch({
            currentOvertimeType: overtimeType,
          })
        );
      })
    );
  }

  @Action(CreateOvertimeType)
  @OnSuccess({ url: AdminWorkAndRestRoutes.OvertimeTypes, message: 'overtimeTypeCreated' })
  createOvertimeType(
    { setState }: StateContext<OvertimeTypeStateModel>,
    { overtimeType }: CreateOvertimeType
  ) {
    return this.service.createOvertimeType(overtimeType).pipe(
      tap((response: IOvertimeType) => {
        setState(
          patch({
            overtimeTypes: insertItem<IOvertimeType>(response),
          })
        );
      })
    );
  }

  @Action(UpdateOvertimeType)
  @OnSuccess({ url: AdminWorkAndRestRoutes.OvertimeTypes, message: 'overtimeTypeUpdated' })
  updateOvertimeType(
    { setState }: StateContext<OvertimeTypeStateModel>,
    { overtimeType }: UpdateOvertimeType
  ) {
    return this.service.updateOvertimeType(overtimeType).pipe(
      tap(() => {
        setState(
          patch({
            overtimeTypes: updateItem<IOvertimeType>(
              (toUpdate) => toUpdate?.id === overtimeType.id,
              overtimeType
            ),
          })
        );
      })
    );
  }

  @Action(DeleteOvertimeType, { cancelUncompleted: true })
  @OnSuccess({ url: AdminWorkAndRestRoutes.OvertimeTypes, message: 'overtimeTypeDeleted' })
  deleteOvertimeType(ctx: StateContext<OvertimeTypeStateModel>, { id }: DeleteOvertimeType) {
    return this.service.deleteOvertimeType(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            overtimeTypes: removeItem<IOvertimeType>(
              (jtToDelete: IOvertimeType | undefined) => jtToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentOvertimeType)
  clearCurrentOvertimeType(ctx: StateContext<OvertimeTypeStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentOvertimeType: undefined,
    });
  }

  @Selector()
  static overtimeTypesTotalPages(state: OvertimeTypeStateModel) {
    return state.overtimeTypesTotalPages;
  }

  @Selector()
  static overtimeTypes(state: OvertimeTypeStateModel) {
    return state.overtimeTypes;
  }

  @Selector()
  static allOvertimeTypes(state: OvertimeTypeStateModel) {
    return state.allOvertimeTypes;
  }

  @Selector()
  static getCurrentOvertimeType(state: OvertimeTypeStateModel) {
    return state.currentOvertimeType;
  }
}
